import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import api, { apiErr } from "@/lib/api";

const MATTERS = ["Civil Matter", "Criminal Matter", "Matrimonial / Family Matter", "Property & Land Matter",
  "Consumer Matter", "Contract / Agreement", "Partnership Deed", "Society Registration / Bylaws",
  "Legal Documentation", "Legal Notice", "Legal Consultation", "Other Legal Matter"];

export function EnquiryForm({ compact }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "", preferred_contact: "Phone" });
  const [captcha, setCaptcha] = useState({ token: "", question: "", answer: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const loadCaptcha = () => {
    api.get("/captcha").then((r) => setCaptcha({ token: r.data.token, question: r.data.question, answer: "" }));
  };
  useEffect(() => { loadCaptcha(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!/^[0-9+\-\s()]{6,20}$/.test(form.phone)) return toast.error("Please enter a valid phone number.");
    if (!form.subject) return toast.error("Please select the nature of your legal matter.");
    if (!form.message.trim()) return toast.error("Please enter your message.");
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, captcha_token: captcha.token, captcha_answer: captcha.answer });
      setDone(true);
      toast.success("Thank you. Your enquiry has been received.");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
      loadCaptcha();
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="bg-white border border-brass/30 rounded-lg p-8 text-center" data-testid="enquiry-success">
        <div className="w-14 h-14 mx-auto rounded-full bg-brass/15 flex items-center justify-center text-brass text-2xl mb-4">✓</div>
        <h3 className="font-serif text-2xl text-navy mb-2">Thank you.</h3>
        <p className="text-slate-600">Your enquiry has been received. Adv. Guruvayoorappan K V's office will review your matter.</p>
        <button onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", subject: "", message: "", preferred_contact: "Phone" }); loadCaptcha(); }}
          className="mt-6 text-brass hover:text-brass-hover font-medium text-sm" data-testid="enquiry-new">
          Submit another enquiry
        </button>
      </div>
    );
  }

  const inputCls = "w-full border border-slate-300 rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-brass focus:ring-1 focus:ring-brass bg-white";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
      <div className={`grid gap-4 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        <div>
          <label className="block text-xs font-semibold text-navy mb-1.5 tracking-wide uppercase">Full Name *</label>
          <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" data-testid="enquiry-name" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-navy mb-1.5 tracking-wide uppercase">Phone Number *</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" data-testid="enquiry-phone" />
        </div>
      </div>
      <div className={`grid gap-4 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        <div>
          <label className="block text-xs font-semibold text-navy mb-1.5 tracking-wide uppercase">Email</label>
          <input className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="enquiry-email" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-navy mb-1.5 tracking-wide uppercase">Nature of Legal Matter *</label>
          <select className={inputCls} value={form.subject} onChange={set("subject")} data-testid="enquiry-subject">
            <option value="">Select a matter</option>
            {MATTERS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-navy mb-1.5 tracking-wide uppercase">Message *</label>
        <textarea rows={compact ? 3 : 4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Briefly describe your legal matter" data-testid="enquiry-message" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-navy mb-1.5 tracking-wide uppercase">Preferred Contact Method</label>
        <div className="flex gap-4 text-sm">
          {["Phone", "Email", "WhatsApp"].map((m) => (
            <label key={m} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="preferred" checked={form.preferred_contact === m} onChange={() => setForm({ ...form, preferred_contact: m })} className="accent-[#C5A059]" />
              {m}
            </label>
          ))}
        </div>
      </div>
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-navy mb-1.5 tracking-wide uppercase">Anti-spam: What is {captcha.question || "…"} ?</label>
          <input className={inputCls} value={captcha.answer} onChange={(e) => setCaptcha({ ...captcha, answer: e.target.value })} placeholder="Answer" data-testid="enquiry-captcha" />
        </div>
        <button type="button" onClick={loadCaptcha} className="mb-1 p-3 text-slate-400 hover:text-brass" aria-label="Refresh captcha" data-testid="enquiry-captcha-refresh"><RefreshCw size={16} /></button>
      </div>
      <button type="submit" disabled={submitting}
        className="w-full bg-navy hover:bg-navy-light text-ivory font-semibold py-3.5 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60 tracking-wide" data-testid="enquiry-submit">
        {submitting && <Loader2 size={18} className="animate-spin" />} Submit Enquiry
      </button>
      <p className="text-xs text-slate-400 text-center">Submitting an enquiry does not create an advocate–client relationship.</p>
    </form>
  );
}
